import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { TrendingUp, Loader2, ArrowLeft, Mail } from 'lucide-react';
import { toast } from 'sonner';

const ForgotPassword = () => {
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);
    try {
      await forgotPassword(email);
      setSubmitted(true);
      toast.success('Reset instructions sent!');
    } catch (error) {
      const message = error.response?.data?.detail?.message || error.response?.data?.detail || 'Failed to send reset email';
      toast.error(typeof message === 'object' ? JSON.stringify(message) : message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4 relative">
      {/* Grid overlay */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: `
          linear-gradient(to right, #1F1F1F 1px, transparent 1px),
          linear-gradient(to bottom, #1F1F1F 1px, transparent 1px)
        `,
        backgroundSize: '40px 40px'
      }} />

      <div className="w-full max-w-md relative z-10 animate-fade-in-up">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 bg-[#00FF94] rounded-lg flex items-center justify-center">
            <TrendingUp className="w-6 h-6 text-black" strokeWidth={2.5} />
          </div>
          <span className="font-bold text-2xl text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
            CoinPulse
          </span>
        </Link>

        <Card className="border-[#1F1F1F] bg-[#0A0A0A]">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl font-bold text-center text-white">
              {submitted ? 'Check your email' : 'Reset password'}
            </CardTitle>
            <CardDescription className="text-center text-[#A1A1AA]">
              {submitted 
                ? 'We sent password reset instructions to your email' 
                : 'Enter your email and we\'ll send you reset instructions'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {submitted ? (
              <div className="text-center space-y-6">
                <div className="w-16 h-16 mx-auto bg-[#00FF94]/10 rounded-full flex items-center justify-center">
                  <Mail className="w-8 h-8 text-[#00FF94]" />
                </div>
                <p className="text-sm text-[#A1A1AA]">
                  Didn't receive the email? Check your spam folder or{' '}
                  <button 
                    onClick={() => setSubmitted(false)}
                    className="text-[#00FF94] hover:text-[#00FF94]/80 transition-colors"
                  >
                    try again
                  </button>
                </p>
                <Link to="/login">
                  <Button 
                    variant="outline" 
                    className="w-full border-[#1F1F1F] bg-transparent hover:bg-white/5 text-white"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to sign in
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#EDEDED]">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-black border-[#1F1F1F] focus:border-[#00FF94] text-white placeholder:text-[#737373]"
                    data-testid="forgot-password-email-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-[#00FF94] text-black hover:bg-[#00FF94]/90 font-bold h-11"
                  disabled={loading}
                  data-testid="forgot-password-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Reset Instructions'
                  )}
                </Button>

                <Link to="/login" className="block">
                  <Button 
                    type="button"
                    variant="ghost" 
                    className="w-full text-[#A1A1AA] hover:text-white hover:bg-white/5"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to sign in
                  </Button>
                </Link>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;
